import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit,
  UserX,
  UserCheck,
  FileText,
  MessageSquare,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import EditCounselorDialog from '../../components/admin/EditCounselorDialog';

export default function CounselorDetail() {
  const { counselorId } = useParams();
  const navigate = useNavigate();
  const [counselor, setCounselor] = useState(null);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeAssignments: 0,
    completedAssignments: 0,
    pendingAssignments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('students');

  useEffect(() => {
    fetchCounselorDetails();
  }, [counselorId]);

  const fetchCounselorDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCounselorById(counselorId);
      setCounselor(response.counselor);
      setAssignedStudents(response.assignedStudents || []);
      setTasks(response.tasks || []);
      setStats(response.stats || {
        totalStudents: 0,
        activeAssignments: 0,
        completedAssignments: 0,
        pendingAssignments: 0,
      });
    } catch (error) {
      toast.error('Failed to fetch counselor details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    try {
      const newStatus = !counselor.isActive;
      await adminAPI.updateCounselorStatus(counselorId, newStatus);
      toast.success(`Counselor ${newStatus ? 'activated' : 'deactivated'}`);
      fetchCounselorDetails();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      'in-progress': { bg: 'bg-blue-100', text: 'text-blue-700' },
      completed: { bg: 'bg-green-100', text: 'text-green-700' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700' },
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-700' },
      ASSIGNED: { bg: 'bg-purple-100', text: 'text-purple-700' },
      COMPLETED: { bg: 'bg-green-100', text: 'text-green-700' },
      SUBMITTED: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
      UNDER_REVIEW: { bg: 'bg-orange-100', text: 'text-orange-700' },
    };
    const config = statusConfig[status] || { bg: 'bg-stone-100', text: 'text-gray-700' };
    return (
      <Badge className={`${config.bg} ${config.text} text-xs`}>
        {status?.replace(/_/g, ' ') || 'Unknown'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!counselor) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <AlertCircle className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Counselor Not Found</h2>
          <p className="text-gray-500 mb-4">The counselor you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/admin/counselors')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Counselors
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/counselors')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Counselor Details</h1>
            <p className="text-gray-600">View and manage counselor information</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 bg-gradient-to-br from-[#F2575F] to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                {counselor.firstName?.[0]}{counselor.lastName?.[0]}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {counselor.firstName} {counselor.lastName}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${
                    counselor.role === 'advisor'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {counselor.role === 'advisor' ? 'Advisor' : 'Counselor'}
                  </Badge>
                  <Badge className={`${
                    counselor.isActive !== false
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {counselor.isActive !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span>{counselor.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>{counselor.phone || 'Not provided'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Joined {formatDate(counselor.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="md:ml-auto flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
              <Button
                variant={counselor.isActive !== false ? 'destructive' : 'default'}
                onClick={handleStatusToggle}
              >
                {counselor.isActive !== false ? (
                  <>
                    <UserX className="w-4 h-4 mr-2" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Activate
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Students</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Active Assignments</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.activeAssignments}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.completedAssignments}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingAssignments}</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <Card className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="students" className="gap-2">
                <Users className="w-4 h-4" />
                Assigned Students ({assignedStudents.length})
              </TabsTrigger>
              <TabsTrigger value="tasks" className="gap-2">
                <FileText className="w-4 h-4" />
                Tasks ({tasks.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="students">
              {assignedStudents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No students assigned</p>
                  <p className="text-sm">Students will appear here once assigned to this counselor</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Services</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Assigned Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignedStudents.map((student, index) => (
                        <TableRow key={student._id || index}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {student.firstName?.[0]}{student.lastName?.[0]}
                              </div>
                              <span className="font-medium">
                                {student.firstName} {student.lastName}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600">{student.email}</TableCell>
                          <TableCell>{student.country || 'N/A'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {student.services?.length || 0} service(s)
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {student.services?.[0]?.status && getStatusBadge(student.services[0].status)}
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {formatDate(student.services?.[0]?.assignedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="tasks">
              {tasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No tasks found</p>
                  <p className="text-sm">Tasks assigned by this counselor will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Due Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tasks.map((task, index) => (
                        <TableRow key={task._id || index}>
                          <TableCell className="font-medium">{task.title}</TableCell>
                          <TableCell>
                            {task.student?.firstName} {task.student?.lastName}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{task.taskType}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${
                              task.priority === 'URGENT' ? 'bg-red-100 text-red-700' :
                              task.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                              task.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-stone-100 text-gray-700'
                            }`}>
                              {task.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(task.status)}</TableCell>
                          <TableCell className="text-gray-600">
                            {task.dueDate ? formatDate(task.dueDate) : 'No due date'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Edit Dialog */}
      <EditCounselorDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        counselor={counselor}
        onSuccess={fetchCounselorDetails}
      />
    </AdminLayout>
  );
}
